import { useEffect, useId, useRef, useState } from "react";
import { getApiClient } from "@/lib/api-client";
import styles from "./PlaceAutocomplete.module.scss";

export interface MapboxPlace {
  placeName: string;
  text: string;
  longitude: number;
  latitude: number;
}

interface Props {
  /** Current text shown in the input. */
  value: string;
  /** Called on every keystroke so the parent can keep its own state. */
  onChange: (text: string) => void;
  /** Called when the user picks a result from the dropdown. */
  onSelect?: (place: MapboxPlace) => void;
  placeholder?: string;
  disabled?: boolean;
  inputId?: string;
  ariaInvalid?: boolean;
  ariaDescribedBy?: string;
}

const DEBOUNCE_MS = 300;
const MIN_QUERY = 2;

/**
 * Debounced place autocomplete. Hits the shared `/geocode` API (Mapbox places
 * type) — same endpoint the mobile `PlaceAutocomplete` uses. Free-text edits
 * always flow through `onChange`; `onSelect` only fires when a dropdown row
 * is chosen.
 */
export function PlaceAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder,
  disabled = false,
  inputId,
  ariaInvalid,
  ariaDescribedBy,
}: Props) {
  const generatedId = useId();
  const id = inputId ?? `place-${generatedId}`;
  const listboxId = `${id}-listbox`;

  const [results, setResults] = useState<MapboxPlace[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);

  const skipNextFetchRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    };
  }, []);

  function search(q: string) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < MIN_QUERY) {
      setResults([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ q, types: "place" });
        const data = await getApiClient().get<MapboxPlace[]>(
          `/geocode?${params.toString()}`,
        );
        setResults(data);
        setOpen(data.length > 0);
        setActiveIdx(-1);
      } catch {
        setResults([]);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const next = e.target.value;
    onChange(next);
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    search(next);
  }

  function handleSelect(place: MapboxPlace) {
    skipNextFetchRef.current = true;
    onChange(place.text);
    onSelect?.(place);
    setOpen(false);
    setResults([]);
    setActiveIdx(-1);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || results.length === 0) {
      if (e.key === "ArrowDown" && results.length > 0) {
        setOpen(true);
        setActiveIdx(0);
        e.preventDefault();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      setActiveIdx((i) => (i + 1) % results.length);
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setActiveIdx((i) => (i <= 0 ? results.length - 1 : i - 1));
      e.preventDefault();
    } else if (e.key === "Enter" && activeIdx >= 0) {
      handleSelect(results[activeIdx]);
      e.preventDefault();
    } else if (e.key === "Escape") {
      setOpen(false);
      setActiveIdx(-1);
    }
  }

  return (
    <div className={styles.wrapper} ref={containerRef}>
      <div className={styles.inputRow}>
        <input
          id={id}
          className={styles.input}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-activedescendant={
            activeIdx >= 0 ? `${id}-opt-${activeIdx}` : undefined
          }
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          autoComplete="off"
          spellCheck={false}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            // Slight delay so a click on a row registers before close.
            blurTimerRef.current = setTimeout(() => setOpen(false), 150);
          }}
          onFocus={() => {
            if (results.length > 0) setOpen(true);
          }}
          placeholder={placeholder}
          disabled={disabled}
        />
        {loading && (
          <span className={styles.spinner} aria-hidden>
            …
          </span>
        )}
      </div>
      {open && results.length > 0 && (
        <ul id={listboxId} role="listbox" className={styles.dropdown}>
          {results.map((place, idx) => (
            <li
              key={`${place.latitude}-${place.longitude}-${place.text}`}
              id={`${id}-opt-${idx}`}
              role="option"
              aria-selected={idx === activeIdx}
              className={`${styles.option} ${
                idx === activeIdx ? styles.optionActive : ""
              }`}
              // onMouseDown fires before the input's onBlur, so the click
              // registers even though blur would otherwise close the list.
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(place);
              }}
            >
              <span className={styles.optionText}>{place.text}</span>
              <span className={styles.optionSub}>{place.placeName}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
